/* eslint-disable no-unused-vars */
import React, { FC, useContext, useState } from 'react';

import Router from 'next/router';
import { Alert, AlertIcon, Box, Flex, Heading } from '@chakra-ui/react';
import SignInForm from '../components/userAuth/SignInForm';
import VerifyCodeForm from '../components/userAuth/VerifyCodeForm';
import NewPasswordForm from '../components/userAuth/NewPasswordForm';
import ForgotPasswordForm from '../components/userAuth/ForgotPasswordForm';
import ResetPasswordForm from '../components/userAuth/ResetPasswordForm';
import { ResendConfirmationCode } from '../libs/authentication';
import { AuthStateContext } from '../contexts/AuthContext';
import Page from '../components/Page';

enum FormStates {
  LOGIN,
  NEW_PASSWORD,
  CONFIRM_EMAIL,
  FORGOT_PASSWORD,
  RESET_PASSWORD
}

const SignIn: FC = () => {
  const [error, setError] = useState('');
  const [tmpUser, setTmpUser]: [any, any] = useState({});
  const [formState, setFormState] = useState(FormStates.LOGIN);
  // @ts-ignore
  const { isAuthenticated } = useContext(AuthStateContext as any);

  const errorMessage = error.length > 0 && (
    <div className='alert alert-danger text-center' role='alert'>
      {error}
    </div>
  );

  const onConfirmEmail = () => {
    setFormState(FormStates.LOGIN);
  };

  async function onLogin(err: string, user: any) {
    setTmpUser(user);

    if (err) {
      if (err === 'FORGOT_PASSWORD') {
        setFormState(FormStates.FORGOT_PASSWORD);
        return;
      }
      if (err === 'NEW_PASSWORD_REQUIRED') {
        setFormState(FormStates.NEW_PASSWORD);
        return;
      }
      if (err === 'PASSWORD_VERIFIER' || err === 'PasswordResetRequiredException') {
        setError('Password reset required. Please reset your password before attempting to sign in.');
        return;
      }
      if (err === 'UserNotConfirmedException') {
        setFormState(FormStates.CONFIRM_EMAIL);
        return;
      }

      console.error({ action: 'login:error', error: { name: '', message: err } });
      setError(err);
      setFormState(FormStates.LOGIN);
      // return;
    }

    // await Router.push('/dashboard').catch((e) => {
    //   console.error({ action: 'onLogin:route:error', error: e });
    //   setError('Could not load account page');
    //   setFormState(FormStates.LOGIN);
    // });
  }

  async function onNewPassword(err: string) {
    if (err) {
      console.error({ action: 'onNewPassword:error', error: { name: '', message: err } });
      setError(err);
      setFormState(FormStates.LOGIN);
      return;
    }

    await Router.push('/dashboard').catch((e) => {
      console.error({ action: 'onNewPassword:route:error', error: e });
      setError('Could not load account page');
      setFormState(FormStates.LOGIN);
    });
  }

  async function onPasswordForgot(err: string, data?: { email: string }) {
    // Resend confirmation code
    if (err === 'Email not yet confirmed') {
      const email = data?.email as unknown as string;

      await ResendConfirmationCode({ email });

      setTmpUser({ username: email });
      setFormState(FormStates.CONFIRM_EMAIL);
      return;
    }

    if (err) {
      console.error({ action: 'onPasswordForgot:error', error: { name: '', message: err } });
      setError(err);
    }

    if (!data) {
      setFormState(FormStates.LOGIN);
      return;
    }

    setTmpUser({ username: data.email });

    setFormState(FormStates.RESET_PASSWORD);
  }

  async function onPasswordReset(err: string) {
    if (err) {
      console.error({ action: 'onPasswordReset:error', error: { name: '', message: err } });
      setError(err);
    }
    setFormState(FormStates.FORGOT_PASSWORD);
  }

  function goToAccountPage() {
    // Router.push('/dashboard').catch((e) => {
    //   console.error({ action: 'onLogin:route:error', error: e });
    //   setError('Could not load account page');
    //   setFormState(FormStates.LOGIN);
    // });
  }

  return (
    <Page
      seoOptions={{
        title: 'Sign in for Effortless Data Migration and Onboarding',
        description:
          'Sign in to your SmartParse account to manage your data migrations and access our intuitive mapping editor, comprehensive reporting ' +
          'tools and features. ',
        canonical: 'https://smartparse.io/sign-in/'
      }}
      maxW='100%'
      p={0}
      m={0}
    >
      <>
        {isAuthenticated && goToAccountPage()}
        {error && errorMessage}
        <Flex direction={{ base: 'column', md: 'row' }} overflow='hidden' minH='100vh' height='100%'>
          <Box overflowY='auto' flex='1' py={{ base: '10', md: '16' }} px={{ base: '6', md: '10' }}>
            <Box maxW='sm' mx='auto'>
              <Box textAlign='center' mb={{ base: '10', md: '16' }}>
                <Heading as='h1' size='xl' fontWeight='extrabold' letterSpacing='tight'>
                  Sign in to your account
                </Heading>
                {/* <Text mt='3' color={mode('gray.600', 'gray.400')} fontWeight='medium'>
                {`Need an account? `}
                <UnderlineLink href='/sign-up/'>Sign up for free</UnderlineLink>
              </Text> */}
              </Box>
              {/* Errors */}
              {error.length > 0 && (
                <Alert status='error' mb='4' borderRadius='md'>
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              {/* eslint-disable-next-line react/jsx-no-bind */}
              {formState === FormStates.LOGIN ? <SignInForm callback={onLogin} /> : null}
              {/* eslint-disable-next-line react/jsx-no-bind */}
              {formState === FormStates.NEW_PASSWORD ? (
                // eslint-disable-next-line react/jsx-no-bind
                <NewPasswordForm user={tmpUser} callback={onNewPassword} />
              ) : null}
              {formState === FormStates.CONFIRM_EMAIL ? (
                <VerifyCodeForm user={tmpUser} callback={onConfirmEmail} />
              ) : null}
              {/* eslint-disable-next-line react/jsx-no-bind */}
              {formState === FormStates.FORGOT_PASSWORD ? <ForgotPasswordForm callback={onPasswordForgot} /> : null}
              {formState === FormStates.RESET_PASSWORD ? (
                // eslint-disable-next-line react/jsx-no-bind
                <ResetPasswordForm callback={onPasswordReset} user={tmpUser} />
              ) : null}
            </Box>
          </Box>
        </Flex>
      </>
    </Page>
  );
};

export default SignIn;
